// DeityInterface — a tool's AI presence. As of 2026-07-26 this is a thin
// adapter over the shared <AiModule/>: same layout, same controls and same
// master toggle as SyQi's home module, wired to the per-tool deity thread
// (/api/ai/deity, docs/DEITY_AGENT_BACKEND_DESIGN.md) and the deity's own GLB.
//
// The E dropdown lists the deity's prompt-menu questions (each view offers to
// ask it) plus the live Chat. The old text/voice/video chip row is gone —
// video on/off now lives in the module's ⋮ menu.
import React, { useState } from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { DISPLAY_FONT, makeThemedStyles } from '../v10/theme';
import { GlassCard, Medallion } from '../v10/ui';
import { SEAT_BY_ID, portraitFor } from '../v10/roster';
import { getDeityAvatar, resolveGlbUrl } from './deityAvatars';
import { formKeyForTool, getDeityForm } from './deityFormRegistry';
import { DeityAction, useSyqiChat } from './useSyqiChat';
import { AiModule, type AiModuleView } from './AiModule';

export function DeityInterface({ toolId }: { toolId: string }) {
  const styles = useStyles();
  const entry = getDeityAvatar(toolId);
  const seat = SEAT_BY_ID[toolId];
  const [pendingProposals, setPendingProposals] = useState<Extract<DeityAction, { kind: 'proposeFormFill' }>[]>([]);
  const [wroteLog, setWroteLog] = useState<Extract<DeityAction, { kind: 'wrote' }>[]>([]);

  const formKey = formKeyForTool(toolId);

  const handleActions = (actions: DeityAction[]) => {
    const proposals = actions.filter((a): a is Extract<DeityAction, { kind: 'proposeFormFill' }> => a.kind === 'proposeFormFill');
    const wrote = actions.filter((a): a is Extract<DeityAction, { kind: 'wrote' }> => a.kind === 'wrote');
    if (proposals.length > 0) setPendingProposals((prev) => [...prev, ...proposals]);
    if (wrote.length > 0) setWroteLog((prev) => [...prev, ...wrote]);
  };

  const { messages, sending, loadingMessages, send: sendMessage } = useSyqiChat({
    toolId,
    endpoint: '/api/ai/deity',
    onActions: handleActions,
  });

  if (!entry) return null;

  const send = async (message: string) => {
    const adapter = formKey ? getDeityForm(formKey) : undefined;
    const currentValues = adapter?.getCurrentValues();
    const formContext = formKey && currentValues ? { formKey, currentValues } : undefined;
    await sendMessage(message, formContext);
  };

  const applyProposal = (proposal: Extract<DeityAction, { kind: 'proposeFormFill' }>) => {
    const adapter = getDeityForm(proposal.formKey);
    if (adapter) adapter.applyFill(proposal.fields, proposal.recordId);
    setPendingProposals((prev) => prev.filter((p) => p !== proposal));
  };

  const discardProposal = (proposal: Extract<DeityAction, { kind: 'proposeFormFill' }>) => {
    setPendingProposals((prev) => prev.filter((p) => p !== proposal));
  };

  const views: AiModuleView[] = [
    { key: 'chat', label: 'Chat' },
    ...entry.promptMenu.map((item) => ({
      key: item.clipSet,
      label: item.label,
      speakText: item.label,
      render: () => (
        <GlassCard style={styles.promptCard}>
          {portraitFor(toolId) ? (
            <View style={styles.promptHead}>
              <Medallion id={toolId} size={40} />
              <View style={styles.promptHeadText}>
                <Text style={styles.promptName}>{entry.deityName}</Text>
                {seat ? <Text style={styles.promptAbility}>the ability to {seat.ability}</Text> : null}
              </View>
            </View>
          ) : null}
          <Text style={styles.promptQuestion}>{item.label}</Text>
          <Pressable style={styles.promptAsk} onPress={() => send(item.label)} disabled={sending}>
            <Text style={styles.promptAskText}>Ask {entry.deityName}</Text>
          </Pressable>
        </GlassCard>
      ),
    })),
  ];

  // Write receipts and pending proposals land alongside the latest reply —
  // neither action carries a message index, so they tail the chat stream.
  const chatExtras = (
    <>
      {wroteLog.map((w, i) => (
        <View key={`w-${i}-${w.rowId ?? i}`} style={styles.wroteRow}>
          <Text style={styles.wroteText}>✓ {w.summary}</Text>
        </View>
      ))}
      {pendingProposals.map((proposal, i) => (
        <GlassCard key={`${proposal.formKey}-${proposal.recordId ?? i}-${i}`} style={styles.proposalCard}>
          <Text style={styles.proposalSummary}>
            {entry.deityName} suggests: {proposal.summary}
          </Text>
          <View style={styles.proposalActions}>
            <Pressable style={styles.proposalDiscard} onPress={() => discardProposal(proposal)}>
              <Text style={styles.proposalDiscardText}>Discard</Text>
            </Pressable>
            <Pressable style={styles.proposalApply} onPress={() => applyProposal(proposal)}>
              <Text style={styles.proposalApplyText}>Apply</Text>
            </Pressable>
          </View>
        </GlassCard>
      ))}
    </>
  );

  return (
    <AiModule
      stateKey={`tool.${toolId}`}
      title={entry.deityName}
      defaultEnabled={false}
      accentColor={entry.color}
      views={views}
      messages={messages}
      sending={sending}
      loadingMessages={loadingMessages}
      onSend={send}
      placeholder={`Message ${entry.deityName}`}
      emptyChatText={`Ask ${entry.deityName} about this tool.`}
      chatExtras={chatExtras}
      glbUrl={resolveGlbUrl(toolId)}
    />
  );
}

const useStyles = makeThemedStyles(({ t }) =>
  StyleSheet.create({
    promptCard: {
      backgroundColor: t.glass,
      borderWidth: 1,
      borderColor: t.navyLine,
      borderRadius: 22,
      padding: 14,
      gap: 10,
    },
    promptHead: { flexDirection: 'row', alignItems: 'center', gap: 12 },
    promptHeadText: { flex: 1, gap: 2 },
    promptName: { fontFamily: DISPLAY_FONT, fontSize: 16, letterSpacing: 2, color: t.ivory, textTransform: 'uppercase' },
    promptAbility: { fontSize: 12, fontStyle: 'italic', color: t.ivorySoft },
    promptQuestion: { fontSize: 14, fontWeight: '700', color: t.ivory },
    promptAsk: {
      alignSelf: 'flex-start',
      backgroundColor: t.gold,
      borderRadius: 999,
      paddingHorizontal: 14,
      paddingVertical: 7,
    },
    promptAskText: { fontSize: 12, fontWeight: '900', color: t.onGold },

    wroteRow: { alignSelf: 'stretch', paddingHorizontal: 4, paddingVertical: 2 },
    wroteText: { fontSize: 12, color: t.ivorySoft, fontStyle: 'italic' },
    proposalCard: {
      alignSelf: 'stretch',
      backgroundColor: t.glass,
      borderWidth: 1,
      borderColor: t.navyLine,
      borderRadius: 22,
      padding: 12,
      gap: 8,
    },
    proposalSummary: { fontSize: 13, color: t.ivory },
    proposalActions: { flexDirection: 'row', justifyContent: 'flex-end', gap: 8 },
    proposalDiscard: {
      backgroundColor: t.navyRaised,
      borderWidth: 1,
      borderColor: t.navyLine,
      borderRadius: 999,
      paddingHorizontal: 14,
      paddingVertical: 6,
    },
    proposalDiscardText: { fontSize: 12, fontWeight: '700', color: t.ivorySoft },
    proposalApply: {
      backgroundColor: t.gold,
      borderRadius: 999,
      paddingHorizontal: 14,
      paddingVertical: 6,
    },
    proposalApplyText: { fontSize: 12, fontWeight: '900', color: t.onGold },
  }),
);

export default DeityInterface;
